import React, { useState } from "react";
import { Declaration } from "../../types/declaration";
import { getApplicantName, getDeclarationStatus, getFsaLink, getStatusColor } from "../../utils/declarationUtils";
import { formatDate } from '../../utils/dateUtils';
import '../Certificates/Certificates.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ColumnFilter from '../Filters/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';

const DATE_COLUMNS = ['reg_date', 'end_date', 'termination_date'];

// Значение ячейки в виде текста: для Excel и для обрезки длинных значений
const getCellText = (item: Declaration, columnKey: string): string => {
    if (columnKey === 'status') return getDeclarationStatus(item);
    if (columnKey === 'applicant_short_name') return getApplicantName(item);
    const value = item[columnKey as keyof Declaration];
    if (value === null || value === undefined) return '';
    if (DATE_COLUMNS.includes(columnKey)) return formatDate(String(value));
    return String(value);
};

const exportToExcel = (data: Declaration[], visibleColumns: string[], fileName: string) => {
    const columnMapping: { [key: string]: string } = {
        'reg_date': 'Дата регистрации декларации',
        'end_date': 'Дата окончания декларации',
        'status': 'Статус декларации',
        'decl_schema': 'Схема декларирования',
        'reg_number': 'Номер декларации',
        'applicant_short_name': 'Заявитель',
        'applicant_inn': 'ИНН заявителя',
        'manufacturer_full_name': 'Изготовитель',
        'manufacturer_inn': 'ИНН изготовителя',
        'product_kind': 'Вид продукции',
        'product_full_name': 'Продукция',
        'tnved_code': 'Код ТН ВЭД',
        'tech_documents': 'Изготовлено в соответствии',
        'standards': 'Стандарты',
        'lab_names': 'Названия лабораторий',
        'lab_reg_numbers': 'Номера лабораторий',
        'protocol_numbers': 'Номера протоколов',
        'termination_date': 'Дата прекращения',
        'termination_reason': 'Причина прекращения'
    };

    const formattedData = data.map(item => {
        const formattedItem: { [key: string]: string } = {};
        visibleColumns.forEach(columnKey => {
            const label = columnMapping[columnKey];
            if (label) {
                formattedItem[label] = getCellText(item, columnKey);
            }
        });
        return formattedItem;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Декларации");

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(dataBlob, `${fileName}.xlsx`);
};

interface DeclarationsTableProps {
    declarations: Declaration[];
    // Полный список для выгрузки в Excel, если в таблице показана одна страница
    exportDeclarations?: Declaration[];
    exportFileName?: string;
    // Без onSort заголовки не кликаются (например, в комплексной проверке)
    onSort?: (field: keyof Declaration) => void;
    getSortIcon?: (field: keyof Declaration) => string;
    onRowClick?: (declaration: Declaration) => void;
}

interface ColumnConfig {
    key: keyof Declaration;
    label: string;
    render?: (value: any, item: Declaration) => React.ReactNode;
}

// Длинные тексты (стандарты, продукция) обрезаем до нескольких строк, полный текст - в подсказке
const LONG_TEXT_LENGTH = 120;

function DeclarationsTable({ declarations, exportDeclarations, exportFileName = 'Декларации', onSort, getSortIcon, onRowClick }: DeclarationsTableProps) {
    const allColumnKeys = [
        'reg_date',
        'end_date',
        'status',
        'decl_schema',
        'reg_number',
        'applicant_short_name',
        'applicant_inn',
        'manufacturer_full_name',
        'manufacturer_inn',
        'product_kind',
        'product_full_name',
        'tnved_code',
        'tech_documents',
        'standards',
        'lab_names',
        'lab_reg_numbers',
        'protocol_numbers',
        'termination_date',
        'termination_reason'
    ];

    // Громоздкие столбцы по умолчанию скрыты, включаются через "Столбцы"
    const hiddenByDefault = ['tech_documents', 'standards', 'lab_reg_numbers', 'protocol_numbers', 'termination_date', 'termination_reason'];
    const defaultColumns = allColumnKeys.filter(columnKey => !hiddenByDefault.includes(columnKey));

    // Минимальная ширина столбцов: таблица становится шире экрана и прокручивается по горизонтали,
    // как у сертификатов, а не сжимает текст в узкие столбцы
    const columnMinWidths: { [key: string]: number } = {
        'reg_date': 110,
        'end_date': 110,
        'status': 120,
        'decl_schema': 120,
        'reg_number': 160,
        'applicant_short_name': 160,
        'applicant_inn': 120,
        'manufacturer_full_name': 180,
        'manufacturer_inn': 120,
        'product_kind': 220,
        'product_full_name': 320,
        'tnved_code': 110,
        'tech_documents': 280,
        'standards': 320,
        'lab_names': 260,
        'lab_reg_numbers': 140,
        'protocol_numbers': 160,
        'termination_date': 110,
        'termination_reason': 220
    };

    const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
        const saved = localStorage.getItem('declarations-visible-columns');
        if (saved) {
            // Испорченное значение в localStorage не должно ронять таблицу
            try {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed)) {
                    return parsed.filter((columnKey: string) => allColumnKeys.includes(columnKey));
                }
            } catch (err) {
                console.error('Ошибка чтения настроек столбцов деклараций:', err);
            }
        }
        localStorage.setItem('declarations-visible-columns', JSON.stringify(defaultColumns));
        return defaultColumns;
    });

    const columns: ColumnConfig[] = [
        { key: 'reg_date', label: 'Дата регистрации декларации' },
        { key: 'end_date', label: 'Дата окончания декларации' },
        {
            key: 'status',
            label: 'Статус декларации',
            render: (value, item) => {
                const status = getDeclarationStatus(item);
                return (
                    <span style={{
                        color: 'white',
                        backgroundColor: getStatusColor(status),
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '11px',
                        fontWeight: '500'
                    }}>
                        {status}
                    </span>
                );
            }
        },
        { key: 'decl_schema', label: 'Схема декларирования' },
        {
            key: 'reg_number',
            label: 'Номер декларации',
            render: (value, item) => {
                const fsaLink = getFsaLink(item);
                return fsaLink ? (
                    <a
                        href={fsaLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        {value}
                    </a>
                ) : (
                    value
                );
            }
        },
        { key: 'applicant_short_name', label: 'Заявитель' },
        { key: 'applicant_inn', label: 'ИНН заявителя' },
        { key: 'manufacturer_full_name', label: 'Изготовитель' },
        { key: 'manufacturer_inn', label: 'ИНН изготовителя' },
        { key: 'product_kind', label: 'Вид продукции' },
        { key: 'product_full_name', label: 'Продукция' },
        { key: 'tnved_code', label: 'Код ТН ВЭД' },
        { key: 'tech_documents', label: 'Изготовлено в соответствии' },
        { key: 'standards', label: 'Стандарты' },
        { key: 'lab_names', label: 'Названия лабораторий' },
        { key: 'lab_reg_numbers', label: 'Номера лабораторий' },
        { key: 'protocol_numbers', label: 'Номера протоколов' },
        { key: 'termination_date', label: 'Дата прекращения' },
        { key: 'termination_reason', label: 'Причина прекращения' }
    ];

    const handleColumnVisibilityChange = (newVisibleColumns: string[]) => {
        setVisibleColumns(newVisibleColumns);
        localStorage.setItem('declarations-visible-columns', JSON.stringify(newVisibleColumns));
    };

    const resetColumnSettings = () => {
        setVisibleColumns(defaultColumns);
        localStorage.setItem('declarations-visible-columns', JSON.stringify(defaultColumns));
    };

    const filteredColumnOrder = allColumnKeys.filter(columnKey => visibleColumns.includes(columnKey));

    const getColumnByKey = (key: string) => columns.find(column => column.key === key);

    const { getStyleForColumn, handleMouseDown } = useResizableColumns(filteredColumnOrder, {
        storageKey: 'declarations-column-widths',
        minWidthPx: 100,
        maxWidthPx: 800
    });

    const renderCell = (column: ColumnConfig, item: Declaration) => {
        if (column.render) {
            return column.render(item[column.key], item);
        }
        const text = getCellText(item, column.key);
        if (text.length > LONG_TEXT_LENGTH) {
            return (
                <div
                    title={text}
                    style={{ display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                    {text}
                </div>
            );
        }
        return text;
    };

    return (
        <div className="table-container">
            <div className="table-controls">
                <button
                    onClick={() => exportToExcel(exportDeclarations || declarations, filteredColumnOrder, exportFileName)}
                    className="export-button-certificates"
                >
                    Экспортировать в Excel
                </button>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <ColumnFilter
                        columns={columns}
                        visibleColumns={visibleColumns}
                        onColumnVisibilityChange={handleColumnVisibilityChange}
                        defaultColumns={defaultColumns}
                        onReset={resetColumnSettings}
                    />
                </div>
            </div>

            {declarations.length === 0 ? (
                <div className="no-data-message">
                    <p>Нет данных для отображения</p>
                </div>
            ) : (
                <table className="lab-table" style={{ fontSize: '13px' }}>
                    <thead>
                        <tr className='table-header_detail'>
                            {filteredColumnOrder.map((columnKey) => {
                                const column = getColumnByKey(columnKey);
                                if (!column) return null;

                                return (
                                    <th
                                        key={columnKey}
                                        className="table-header-item"
                                        onClick={() => onSort?.(column.key)}
                                        style={{ padding: '4px', cursor: onSort ? 'pointer' : 'default', minWidth: columnMinWidths[columnKey], ...getStyleForColumn(columnKey) }}
                                    >
                                        {column.label} {getSortIcon ? getSortIcon(column.key) : ''}
                                        <span
                                            className="col-resizer"
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => handleMouseDown(columnKey, e)}
                                        />
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {declarations.map((declaration) => (
                            <tr
                                className={`table-body_detail${onRowClick ? ' table-row-clickable' : ''}`}
                                key={declaration.reg_number}
                                onClick={() => onRowClick?.(declaration)}
                                onKeyDown={(event) => {
                                    if (!onRowClick) return;
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onRowClick(declaration);
                                    }
                                }}
                                tabIndex={onRowClick ? 0 : -1}
                                role={onRowClick ? 'button' : undefined}
                                aria-label={
                                    onRowClick
                                        ? `Подробнее о декларации ${declaration.reg_number}`
                                        : undefined
                                }
                            >
                                {filteredColumnOrder.map((columnKey) => {
                                    const column = getColumnByKey(columnKey);
                                    if (!column) return null;

                                    return (
                                        <td key={columnKey} className="table-body-item" style={{ padding: '4px', ...getStyleForColumn(columnKey) }}>
                                            {renderCell(column, declaration)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default DeclarationsTable;
